import React from 'react';
import _ from 'lodash-es';
import Select from 'react-select';
import { getSubscriptionPlan, createOrUpdateSubscriptionPlan, postCreateVoucherifyProduct, updateVoucherifyProduct } from '../api';
import currencyUtils, { ALL_CURRENCIES } from '../helpers/currencyUtils';
import { Button, ButtonGroup, Link, SelectFieldWithLabel, TextFieldWithLabel, NumberFieldWithLabel, StatusMessage } from '../components/library-components';
import validate from 'validate.js';

const LIFETIMEVALUE = 'lifetime';
const LIFETIMELABEL = 'lifetime (100 Years)';
const DURATION_UNITS_ONE_TIME = ['weeks', 'months', 'years', LIFETIMELABEL].map((k) => ({ label: k, value: k === LIFETIMELABEL ? LIFETIMEVALUE : k }));
const ACTIVE_INACTIVE = ['Active', 'Inactive'].map((k) => ({ label: k, value: k === 'Active' }));
const HIDDEN = false;

const getOptions = (item) => {
  return item.map((k) => ({ label: k['name'], value: k['id'] }));
};

export default class PayPerAssetPlan extends React.Component {
  constructor (props) {
    super(props);
    this.state = {
      errors: {},
      priceCents: '0',
      showNotification: { isNotificationOpen: false, status: '', statusMessage: '' },
      voucherifyEnabled: _.get(window.qtConfig, 'voucherifyEnabled', false) === 'true'
    };

    this.updateField = this.updateField.bind(this);
    this.enableSave = this.enableSave.bind(this);
    this.onSave = this.onSave.bind(this);
  }

  componentDidMount () {
    this.setState({ assetCategoryOptions: getOptions(this.props.assetCategories) });
    this.loadSubscriptionPlan(this.props.subscriptionPlanId);
  }

  loadSubscriptionPlan () {
    const defaults = { title: 'Pay per asset', price_currency: 'INR', duration_unit: LIFETIMEVALUE, duration_length: 1, display_assets: [{ asset_category_id: this.props.assetCategories.filter(category => category.name === 'story')[0].id, title: 'An individually purchasable asset' }] };

    getSubscriptionPlan(this.props.subscriptionGroupId, this.props.subscriptionPlanId)
      .then(plan => this.setState({ priceCents: currencyUtils.convertToCurrencyUnits(_.get(plan, 'price_cents', 0)),
        subscriptionPlan: this.props.subscriptionPlanId === 'new' ? Object.assign({}, plan, defaults) : plan }));
  }

  onSave () {
    const errors = validate(this.state.subscriptionPlan, constraints);
    if (errors) {
      this.setState({ errors });
      return;
    }

    createOrUpdateSubscriptionPlan(this.props.subscriptionGroupId, this.state.subscriptionPlan).then((response) => {
      this.props.afterSave();
    }, (errorResponse) => {
      this.props.afterSave(errorResponse.errors);
    });
  }

  enableSave () {
    if ((this.state.subscriptionPlan.price_cents !== '') && !_.isEmpty(this.state.subscriptionPlan.duration_length) && (this.state.subscriptionPlan.duration_length !== '') && !_.isEmpty(this.state.subscriptionPlan.duration_unit) && !_.isEmpty(this.state.subscriptionPlan.display_assets)) {
      this.setState({ showSave: true });
    } else {
      this.setState({ showSave: true });
    }

    const errors = validate(this.state.subscriptionPlan, constraints);
    this.setState({ errors });
  }

  updateField (field, value) {
    let asset = this.state.subscriptionPlan.display_assets[0];
    switch (field) {
      case 'asset_category_id':
        if (asset === undefined) {
          asset = {
            asset_category_id: value,
            title: 'An individually purchasable asset'
          };
        } else {
          asset = Object.assign({}, asset, { [field]: value });
        }
        this.setState({ subscriptionPlan: Object.assign({}, this.state.subscriptionPlan, { display_assets: [asset] }) }, this.enableSave);
        break;
      case 'asset_metadata':
        if (value.trim() !== '') {
          asset = Object.assign({}, asset, { metadata: { collection_id: value } });
        } else {
          asset = Object.assign({}, asset, { metadata: {} });
        }
        this.setState({ subscriptionPlan: Object.assign({}, this.state.subscriptionPlan, { display_assets: [asset] }) });
        break;
      case 'duration_unit':
        let durationLength = this.state.subscriptionPlan.duration_length;
        if (value === LIFETIMEVALUE) {
          durationLength = 1;
        }
        this.setState({ subscriptionPlan: Object.assign({}, this.state.subscriptionPlan, { [field]: value, duration_length: durationLength }) }, this.enableSave);
        break;
      case 'price_cents':
        this.setState({
          priceCents: value,
          subscriptionPlan: Object.assign({}, this.state.subscriptionPlan, { [field]: currencyUtils.convertToLowestDenomination(value) })
        }, this.enableSave);
        break;
      default:
        this.setState({
          subscriptionPlan: Object.assign({}, this.state.subscriptionPlan, { [field]: value })
        }, this.enableSave);
    }
  }

  render () {
    const plan = this.state.subscriptionPlan;
    const { errors } = this.state;
    if (!plan) {
      return <div />;
    }

    return (<div className='assets-container'>
      <div className='form-row'>
        <SelectFieldWithLabel
          label='Asset Type'
          value={_.get(plan, ['display_assets', '0', 'asset_category_id'], '')}
          onChange={(e) => this.updateField('asset_category_id', e.value)}
          clearable={false}
          options={this.state.assetCategoryOptions}
          disabled={plan.id}
          errors={_.get(errors, ['display_assets', '0'])}
          dataTest='asset_type'
        />

        { HIDDEN && (_.get(plan, ['display_assets', '0', 'asset_category_id'], '') === this.getIdForStoryAssetCategory()) &&
        <TextFieldWithLabel
          label='Belongs to Collection'
          value={_.get(plan, ['display_assets', '0', 'metadata', 'collection_id'], '')}
          onChange={(e) => this.updateField('asset_metadata', e.target.value)}
          placeholder='Collection Id here, leave blank if not required'
          dataTest='collection_id'
        /> }
      </div>

      <div className='form-row'>
        <TextFieldWithLabel
          label='Duration'
          value={_.get(plan, 'duration_unit') === LIFETIMEVALUE ? 1 : _.get(plan, 'duration_length')}
          onChange={(e) => this.updateField('duration_length', e.target.value)}
          errors={_.get(errors, ['duration_length', '0'])}
          disabled={_.get(plan, 'duration_unit') === LIFETIMEVALUE}
          dataTest='duration' />
        <SelectFieldWithLabel
          label='Time Unit'
          value={_.get(plan, 'duration_unit', '')}
          options={DURATION_UNITS_ONE_TIME}
          onChange={(m) => this.updateField('duration_unit', m.value)}
          clearable={false}
          errors={_.get(errors, ['duration_unit', '0'])}
          dataTest='duration_unit' />
      </div>

      <div className='form-row'>
        <div className='text-and-select'>
          <NumberFieldWithLabel
            label='price'
            value={this.state.priceCents}
            onChange={(e) => this.updateField('price_cents', e.target.value)}
            placeholder={'Enter price'}
            errors={_.get(errors, ['price_cents', '0'])}
            dataTest='price_cents' />
          <Select
            className='select-currency'
            value={_.get(plan, 'price_currency', '')}
            options={ALL_CURRENCIES}
            onChange={(m) => this.updateField('price_currency', m.value)}
            clearable={false}
            searchable={true}
            dataTest='price_currency'
            arrowRenderer={currencyUtils.arrowRenderer}
          />
        </div>

        <SelectFieldWithLabel label='Status' value={plan.enabled} options={ACTIVE_INACTIVE} onChange={(e) => this.updateField('enabled', e.value)} id='enabled' clearable={false} />
      </div>

      { this.state.voucherifyEnabled &&
        <div className='form-row'>
          <TextFieldWithLabel label='Voucherify Product ID'
            placeholder='This plan is not linked with Voucherify'
            disabled
            dataTest='voucherify-product-id'
            value={_.get(plan.metadata, ['voucherify', 'product_id'], '')} />
        </div>
      }

      { this.state.voucherifyEnabled && plan.id && _.get(plan, ['metadata', 'voucherify', 'product_id']) &&
        <Link onClick={(e) => this.updateVoucherifyPlan(e, plan.id)} dataTest='voucherify-update'>
          Update Voucherify
        </Link>
      }

      { this.state.voucherifyEnabled && plan.id &&
         !_.get(plan, ['metadata', 'voucherify', 'product_id']) &&
         <Link onClick={(e) => this.createVoucherifyPlan(e, plan.id)} dataTest='voucherify-connect' >
          Connect Voucherify
         </Link>
      }

      <ButtonGroup>
        <Button primary onClick={(e) => this.onSave(e)}>Save</Button>
        <Button onClick={(e) => this.props.onCancel(e)}>Cancel</Button>
      </ButtonGroup>

      {this.state.showNotification.isNotificationOpen &&
        <StatusMessage statusMessage={this.state.showNotification.statusMessage}
          status={this.state.showNotification.status}
          onCancel={() => this.setState({ showNotification: { isNotificationOpen: false, status: '', statusMessage: '' } })} />}
    </div>);
  }

  createVoucherifyPlan (e, subscriptionPlanId) {
    e.preventDefault();
    const createPlan = window.confirm('Do you want to create this plan in Voucherify?');
    if (createPlan === false) return;
    postCreateVoucherifyProduct(subscriptionPlanId)
      .then(response => {
        this.setState({ showNotification: { isNotificationOpen: true, status: 'success', statusMessage: 'Voucherify Product Created Successfully' } });
        this.setState({ subscriptionPlan: Object.assign({}, this.state.subscriptionPlan, { metadata: { voucherify: { product_id: response.voucherify_product_id } } }) });
      },
      (errors) => this.setState({ showNotification: { isNotificationOpen: true, status: 'error', statusMessage: _.get(errors, ['error', 'message']) || _.get(errors, 'errors') } }));
  }

  updateVoucherifyPlan (e, subscriptionPlanId) {
    e.preventDefault();
    const createPlan = window.confirm('Do you want to update this plan in Voucherify?');
    if (createPlan === false) return;
    updateVoucherifyProduct(subscriptionPlanId)
      .then(response => {
        this.setState({ showNotification: { isNotificationOpen: true, status: 'success', statusMessage: 'Voucherify Product Updated Successfully' } });
      },
      (errors) => this.setState({ showNotification: { isNotificationOpen: true, status: 'error', statusMessage: _.get(errors, ['error', 'message']) || _.get(errors, 'errors') } }));
  }

  getIdForStoryAssetCategory () {
    const x = _.find(this.state.assetCategoryOptions, function (o) { return o.label === 'story'; });
    return x['value'];
  }
}

const constraints = {
  display_assets: { presence: { allowEmpty: false, message: 'Required' } },
  price_cents: { presence: true, numericality: { strict: true, lessThanOrEqualTo: 1000000000, greaterThanEqualTo: 0, message: 'Value too large' } },
  duration_length: { presence: true, numericality: { strict: true, onlyInteger: true, greaterThan: 0, lessThanOrEqualTo: 999999 } },
  duration_unit: { presence: { message: 'Cannot be empty' } }
};
